/**
 * Colección `products` — ESQUEMA IDÉNTICO A LA 1.0 (plan, regla 3: byte a byte).
 *
 * Transcrito el 13/10/2026 de C:\DEV\nuvvu:
 *   - functions/marketplace.js → createSellerListing (campos que escribe el servidor al crear)
 *   - functions/productSearchIndex.js → trigger que mantiene search_tokens, category_path,
 *     is_adult, photos_blurred(_key)
 *   - functions/marketplace.js → reserved_*, sold_at, bumped_at, bump_count, is_featured
 *   - firestore.rules → adult_override, featured_*, seller_is_pro, seller_kyc_verified,
 *     validación de vídeo/fotos/campos PRO
 *   - src/pages/CreateProduct.jsx → material, pattern, weight_tier (cliente)
 *
 * Decisiones que NO se cambian aquí aunque choquen con el resto de la 2.0:
 *   - `price` es un número DECIMAL en unidades de `currency` (no Money en céntimos).
 *   - Las fechas son strings ISO (`created_date`), no Timestamp.
 *   - snake_case.
 * Solo se AÑADEN campos opcionales nuevos (sección 7): `translations`, `embedding_ref`,
 * `price_total_hint`, que se rellenan en segundo plano tras el cambio.
 *
 * El schema es `.passthrough()`: si un documento real trae un campo que no está aquí,
 * no se pierde ni falla la validación. Lo que está aquí es lo verificado en el código.
 * 13/10: contrastado con los 6.609 productos reales del sandbox: los campos que la 1.0 no
 * siempre guarda (video_url, seller_kyc_verified, size, color, weight_tier, PRO…) son opcionales.
 * `productDefaults()` rellena lo que el front necesita con los valores de la 1.0.
 */
import { z } from 'zod';
import {
  CountryCodeSchema,
  IsoDateStringSchema,
  LanguageCodeSchema,
  UserIdSchema,
} from './primitives.js';

/**
 * Fecha ISO que en datos migrados de la 1.0 puede venir como Timestamp de Firestore (auditoría 3/11:
 * 216 de 282 vendidos tenían `sold_at` Timestamp → la ficha fallaba la validación y salía
 * «Producto no encontrado»). Se normaliza a string ISO.
 */
const LooseIsoDateSchema = z.preprocess((v) => {
  if (v && typeof v === 'object') {
    const o = v as { toDate?: () => Date; toMillis?: () => number; seconds?: number };
    if (typeof o.toMillis === 'function') return new Date(o.toMillis()).toISOString();
    if (typeof o.toDate === 'function') return o.toDate().toISOString();
    if (typeof o.seconds === 'number') return new Date(o.seconds * 1000).toISOString();
  }
  return v;
}, IsoDateStringSchema);

export const PRODUCT_STATUSES = [
  'draft',
  'pending_review',
  'active',
  'paused',
  'sold',
  'rejected',
] as const;
export const ProductStatusSchema = z.enum(PRODUCT_STATUSES);
export type ProductStatus = z.infer<typeof ProductStatusSchema>;

/** Estados visibles públicamente (firestore.rules 1.0: `status in ['active','sold']`). */
export const PUBLIC_PRODUCT_STATUSES: readonly ProductStatus[] = ['active', 'sold'];

export const PRODUCT_CONDITIONS = ['new', 'like_new', 'good', 'fair', 'poor'] as const;
export const ProductConditionSchema = z.enum(PRODUCT_CONDITIONS);
export type ProductCondition = z.infer<typeof ProductConditionSchema>;

/** Una foto es una URL de Storage o un objeto { url } (PhotoUploader 1.0 admite ambos). */
export const ProductPhotoSchema = z.union([
  z.string().url(),
  z.object({ url: z.string().url() }).passthrough(),
]);
export type ProductPhoto = z.infer<typeof ProductPhotoSchema>;

/** Tramos de peso de shippingTiers.js (1..N); el tramo manda sobre weight_grams. */
export const WeightTierSchema = z.number().int().min(1);

/**
 * D2: piloto automático de ventas por anuncio. `price_drop` baja el precio `percent` % cada
 * `every_days` días hasta `min_price` (precio en unidades, como `products.price`); `auto_accept_min_price`
 * acepta sola cualquier oferta igual o superior. `next_at`/`last_at` los lleva el servidor.
 */
export const AutopilotSchema = z.object({
  price_drop: z
    .object({
      percent: z.number().int().min(1).max(50),
      every_days: z.number().int().min(1).max(30),
      min_price: z.number().positive(),
      next_at: IsoDateStringSchema,
      last_at: IsoDateStringSchema.nullable(),
    })
    .nullable(),
  auto_accept_min_price: z.number().positive().nullable(),
  updated_at: IsoDateStringSchema,
});
export type Autopilot = z.infer<typeof AutopilotSchema>;

export const ProductSchema = z
  .object({
    /** El propio id del documento (createSellerListing hace `set({ ...payload, id: ref.id })`). */
    id: z.string().min(1),
    seller_id: UserIdSchema,
    seller_name: z.string(),
    seller_avatar: z.string(),
    title: z.string().min(1),
    description: z.string(),
    original_language: LanguageCodeSchema,
    /** DECIMAL en unidades de `currency` (1.0). No convertir a céntimos. */
    price: z.number().min(0),
    currency: z.string().regex(/^[A-Z]{3}$/),
    category_id: z.string(),
    category_name: z.string(),
    /** Árbol v2: ruta [raíz, …, hoja]; la mantiene el trigger (null si no se puede resolver). */
    category_path: z.array(z.string()).nullable().optional(),
    brand: z.string(),
    condition: ProductConditionSchema,
    photos: z.array(ProductPhotoSchema).min(1),
    /** Solo URL de Storage en listing_videos/{seller_id}/… o null. */
    video_url: z.string().url().nullable().optional(),
    weight_tier: WeightTierSchema.optional(),
    weight_grams: z.number().min(0),
    length_cm: z.number().nullable().optional(),
    width_cm: z.number().nullable().optional(),
    height_cm: z.number().nullable().optional(),
    offers_enabled: z.boolean().optional(),
    /** Texto plano para el buscador (cliente); los tokens normalizados van en search_tokens. */
    search_keywords: z.string().optional(),
    search_tokens: z.array(z.string()).optional(),
    /** URLs o, en anuncios antiguos, objetos del uploader ({ url, name… }). */
    brand_verification_docs: z
      .array(z.union([z.string(), z.object({ url: z.string() }).passthrough()]))
      .optional(),
    status: ProductStatusSchema,
    /** ISO-2 en mayúsculas en todo lo nuevo; 4 anuncios de la 1.0 traen otra cosa y se conservan. */
    country_code: z.string(),
    region: z.string(),
    city: z.string(),
    /** Instantánea del estado PRO del vendedor al crear/moderar (las reglas validan con él). */
    seller_is_pro: z.boolean(),
    /** Instantánea de `users.seller_type === 'trader'` (etiqueta en tarjeta y ficha; DSA art. 30). */
    seller_is_trader: z.boolean().optional(),
    /** Insignia "identidad verificada" (Stripe Identity). */
    seller_kyc_verified: z.boolean().optional(),
    // Campos solo PRO (validProOnlyFields en reglas): no-PRO → 1 / false / null / null.
    // Los anuncios anteriores a cada función no los traen: opcionales (verificado contra los
    // 6.609 productos reales el 13/10; `pnpm sync:products:verify`).
    stock: z.number().int().min(0).optional(),
    free_shipping: z.boolean().optional(),
    original_price: z.number().min(0).nullable().optional(),
    discount_percent: z.number().min(0).max(100).nullable().optional(),
    visible_countries: z.array(z.string()).optional(),
    vehicle_make: z.string().optional(),
    vehicle_model: z.string().optional(),
    /** Atributos de moda normalizados (fashionAttributes). size ≤ 12 chars, color ≤ 40. */
    size: z.string().max(12).optional(),
    color: z.string().max(40).optional(),
    material: z.string().optional(),
    pattern: z.string().optional(),
    /** Los anuncios ya no caducan (1.0, 2026-10): siempre null salvo anuncios antiguos. */
    expires_at: IsoDateStringSchema.nullable().optional(),
    created_date: IsoDateStringSchema,
    updated_date: IsoDateStringSchema,

    // --- Mantenidos por el servidor (moderación, +18, destacados, reservas, bumps) ---
    moderated_at: z.unknown().optional(), // FieldValue.serverTimestamp() → Timestamp
    moderation_reason: z
      .enum(['prohibited_keywords', 'awaiting_human', 'auto_approved'])
      .or(z.string())
      .nullable()
      .optional(),
    /** Contadores de la 1.0 (los mantienen triggers de Functions; el cliente solo los lee). */
    views: z.number().int().min(0).optional(),
    favorites_count: z.number().int().min(0).optional(),
    is_adult: z.boolean().optional(),
    /** Solo servidor/admin (lib/adultContent.js); el vendedor no puede tocarlo. */
    adult_override: z.enum(['adult', 'safe']).or(z.string()).nullable().optional(),
    photos_blurred: z.array(z.string()).optional(),
    photos_blurred_key: z.string().optional(),
    is_featured: z.boolean().optional(),
    featured_until: z.unknown().optional(),
    featured_scope: z.string().nullable().optional(),
    featured_country: z.string().nullable().optional(),
    featured_homepage: z.boolean().optional(),
    bumped_at: IsoDateStringSchema.optional(),
    bump_count: z.number().int().min(0).optional(),
    sold_at: LooseIsoDateSchema.nullable().optional(),
    /**
     * Marcado vendido a mano: `offline` (a alguien que chateó, `sold_to_user_id`) o `outside_nuvvu`.
     * `nuvvu` lo traen anuncios de la 1.0 vendidos por la plataforma (y alguno reactivado después).
     */
    sold_via: z.enum(['offline', 'outside_nuvvu', 'nuvvu']).optional(),
    sold_to_user_id: z.string().nullable().optional(),
    reserved_order_id: z.string().nullable().optional(),
    reserved_by: z.string().nullable().optional(),
    reserved_until: IsoDateStringSchema.nullable().optional(),

    // --- NUEVOS en la 2.0 (opcionales, backfill en segundo plano; sección 7) ---
    /** D2 piloto automático (lo escribe `listings-setAutopilot`; el cliente solo lo lee). */
    autopilot: AutopilotSchema.optional(),
    /** Por idioma: título siempre (translateTitles, 8 idiomas al publicar); descripción solo si alguien la pidió. */
    translations: z
      .record(
        LanguageCodeSchema,
        z.object({ title: z.string(), description: z.string().optional() }),
      )
      .optional(),
    embedding_ref: z.string().optional(),
    /** Precio total orientativo por país del comprador (artículo + protección + envío), en céntimos. */
    price_total_hint: z.record(CountryCodeSchema, z.number().int()).optional(),
  })
  .passthrough();

export type Product = z.infer<typeof ProductSchema>;

/** Valores por defecto de la 1.0 para los campos que un anuncio antiguo no trae. */
export function productDefaults(p: Product): Product & {
  video_url: string | null;
  weight_tier: number;
  offers_enabled: boolean;
  seller_kyc_verified: boolean;
  stock: number;
  free_shipping: boolean;
  size: string;
  color: string;
} {
  return {
    ...p,
    video_url: p.video_url ?? null,
    weight_tier: p.weight_tier ?? 1,
    offers_enabled: p.offers_enabled ?? true,
    seller_kyc_verified: p.seller_kyc_verified ?? false,
    stock: p.stock ?? 1,
    free_shipping: p.free_shipping ?? false,
    size: p.size ?? '',
    color: p.color ?? '',
  };
}

/** Rutas de la colección. */
export const productsPath = () => 'products' as const;
export const productPath = (productId: string) => `products/${productId}` as const;

// --- Subcolecciones / colecciones satélite que NO cambian (sección 7) ---

export const ProductFavoriteSchema = z
  .object({
    user_id: UserIdSchema,
    product_id: z.string().min(1),
    created_date: IsoDateStringSchema,
  })
  .passthrough();
export type ProductFavorite = z.infer<typeof ProductFavoriteSchema>;
